import { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import i18next from 'i18next';
import { Loader } from '../';

const Chart = ({
  stateSlug,
  totalSeats,
  height = 300,
  pollIntervalMs = 15000,
  lastElection = false,
  // Pre-derived bars ([{name, colors, value}], e.g. deriveChartData over the
  // parent's seatinfo poll). When provided, no chart request is made — mirror
  // of the maps' `fills` prop. When absent, self-fetch as before.
  data,
}) => {
  const [info, setInfo] = useState();
  const [done, setDone] = useState(false);
  const hasData = data != null;

  useEffect(() => {
    if (hasData) return undefined;
    let cancelled = false;
    setDone(false);
    const lang = localStorage.getItem('lang') || 'zh_CN';
    const url = `public-api/chart?state=${stateSlug}&lang=${lang}${
      lastElection ? '&last_election=true' : ''
    }`;
    const fetchChart = async () => {
      try {
        const res = await axios.get(url);
        if (!cancelled) {
          setInfo(res.data || []);
          setDone(true);
        }
      } catch (err) {
        if (!cancelled) setDone(true);
      }
    };
    fetchChart();
    if (lastElection) {
      return () => {
        cancelled = true;
      };
    }
    const interval = setInterval(fetchChart, pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [stateSlug, totalSeats, pollIntervalMs, lastElection, hasData]);

  const bars = hasData ? data : info;
  if (!hasData && !done) return <Loader inline />;
  if (!bars || !bars.length) return null;

  const majority = Math.floor(totalSeats / 2) + 1;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        layout="vertical"
        data={bars}
        margin={{ top: 16, right: 32, left: 8, bottom: 8 }}
        barCategoryGap="20%"
      >
        <XAxis
          type="number"
          domain={[0, totalSeats]}
          tick={{ fontSize: 12, fill: '#666' }}
          axisLine={{ stroke: '#ddd' }}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          tick={{ fontSize: 13, fill: '#333' }}
          width={84}
          axisLine={{ stroke: '#ddd' }}
          tickLine={false}
        />
        <ReferenceLine
          x={majority}
          stroke="#cb0c13"
          strokeDasharray="6 4"
          ifOverflow="visible"
          label={{
            value: `${majority} ${i18next.t('content')}`,
            position: 'top',
            fill: '#cb0c13',
            fontSize: 12,
          }}
        />
        <Bar
          dataKey="value"
          background={{ fill: '#eee', radius: 2 }}
          radius={[2, 2, 2, 2]}
          isAnimationActive={false}
        >
          {bars.map((d, i) => (
            <Cell key={i} fill={d.colors} />
          ))}
          <LabelList
            dataKey="value"
            position="right"
            style={{ fill: '#1a1a1a', fontSize: 13, fontWeight: 600 }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default Chart;
